"use strict";
/**
 * The NBA tap recorder at /app/win-prob/nba/<gameId>/tap?home=MIN&away=DET
 * (ai/nba/N8-taps/PLAN.md). Measurement only; separate from trading.
 *
 * **Timing,** as the football pad (handlers/tap-pad-handler.ts): the touch event's own time on
 * pointerdown, the clock offset from the shortest of five round trips refreshed every minute,
 * and now the sync's age per tap. The queue stamps the send attempt that delivers each tap.
 *
 * **Teams** come from the URL, so the recorder needs no feed or tracker for the game.
 *
 * **Sessions** (N8 §10.1, §10.6): each open starts an unsynchronized session, so taps record at
 * once, and the first clock sync starts a synchronized one carrying that sync's offset: sessions
 * order by corrected start, and a missing offset is never zero. Each session_start goes into the
 * queue behind any unsent taps from before. The free-throw line shows this session's own taps
 * only, starting unknown.
 *
 * **The FT areas** (§11.1, A191) swap between the trip buttons, FT made / FT miss and, while a
 * technical is pending, Tech made / Tech missed (§12.6), as ui/nba-tap-pad.ts nbaFtAreas says. Presses on an area's swapping buttons within GATE_MS of its swap are
 * suppressed (not sent, not recorded), measured on performance.now(), which is monotonic and
 * untouched by the clock sync; the area shows it. FT ?, PULL, UNDO and the other side never wait.
 *
 * **Delivery** (§15): sends time out after SEND_TIMEOUT_MS (tap ids are idempotent); coming back to
 * the front or online retries at once and re-syncs the clock; a screen wake lock is held while
 * open. All of it runs after a tap's time is read.
 *
 * **Timers** are this view's own, started in openNbaTapPad and stopped in closeNbaTapPad, never
 * market polling (ai/GOTCHAS.md §2), never on the order path.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.GATE_MS = void 0;
exports.teamsFromQuery = teamsFromQuery;
exports.nbaPadFromQuery = nbaPadFromQuery;
exports.setupNbaTapPad = setupNbaTapPad;
exports.openNbaTapPad = openNbaTapPad;
exports.closeNbaTapPad = closeNbaTapPad;
const taps_1 = require("../api/taps");
const tap_queue_1 = require("../services/tap-queue");
const parked_hold_1 = require("./parked-hold");
const tap_parked_1 = require("../ui/tap-parked");
const ft_pending_1 = require("../../shared/ft-pending");
const uuid_1 = require("../services/uuid");
const nba_tap_pad_1 = require("../ui/nba-tap-pad");
const tap_vocabulary_1 = require("../../shared/tap-vocabulary");
const FLUSH_MS = 500;
const SYNC_MS = 60000;
const SYNC_SAMPLES = 5;
const REFUSAL_MS = 2500;
/** A tap send unanswered this long fails and is retried (N8 §15). */
const SEND_TIMEOUT_MS = 5000;
/** A clock request unanswered this long ends its sync with the samples it has (ASTRA-REVIEW-162 §1). */
const CLOCK_TIMEOUT_MS = 3000;
/** A swapped FT area ignores its swapping buttons for this long (§11.1, A191). */
exports.GATE_MS = 250;
const TEAM = /^[A-Za-z0-9]{1,5}$/;
let pad = null;
const parkedHold = new parked_hold_1.ParkedHold();
const body = () => document.getElementById("win-prob-body");
const isNbaKind = (v) => typeof v === "string" && tap_vocabulary_1.NBA_TAP_KINDS.includes(v);
const toHistory = (t) => ({
    tapId: t.tapId,
    kind: t.kind,
    side: t.side,
    undoesTapId: t.undoesTapId,
    atMs: Date.parse(t.clientTs),
    sessionId: t.sessionId ?? null,
});
/** Team abbreviations from the URL's query, or HOME and AWAY. */
function teamsFromQuery(search) {
    const q = new URLSearchParams(search);
    const pick = (k, fallback) => {
        const v = q.get(k);
        return v && TEAM.test(v) ? v.toUpperCase() : fallback;
    };
    return { home: pick("home", "HOME"), away: pick("away", "AWAY") };
}
/** The pad version from the query's `pad` (N8 §19.5): 2 only for exactly "2"; absent or invalid is 1. */
function nbaPadFromQuery(search) {
    return new URLSearchParams(search).get("pad") === "2" ? 2 : 1;
}
/** Drops taps the server refused from the pad's own state: its fold, UNDO and lines skip them (N8 §19.3). */
function dropParked(current) {
    const parked = current.queue.parkedIds;
    if (parked.size === 0)
        return;
    current.history = current.history.filter((t) => !parked.has(t.tapId));
    current.sessionTaps = current.sessionTaps.filter((t) => !parked.has(t.tapId));
}
function refreshStatus() {
    if (!pad)
        return;
    dropParked(pad);
    const status = body()?.querySelector(".nba-tap-status");
    const refused = pad.refusal && pad.refusal.until > Date.now() ? pad.refusal.text : null;
    (0, tap_parked_1.showParked)(body()?.querySelector(".tap-parked"), pad.queue.parked.length);
    if (status) {
        status.classList.toggle("tap-refused", refused !== null ? !pad.refusal?.ok : pad.queue.storageFailing);
        if (refused !== null)
            status.textContent = refused;
        else
            status.innerHTML = (0, nba_tap_pad_1.renderNbaTapStatus)({
                storageFailing: pad.queue.storageFailing,
                pending: pad.queue.size,
                failedSends: pad.queue.failedSends,
                offsetMs: pad.clock?.offsetMs ?? null,
                rttMs: pad.clock?.rttMs ?? null,
                syncedAgoS: pad.clock ? Math.round((Date.now() - pad.clock.at) / 1000) : null,
                error: pad.queue.lastError,
                conflicts: pad.queue.conflicts.length,
                screenAwake: pad.wakeLock !== null,
            });
    }
    const ft = body()?.querySelector(".nba-ft-state");
    if (ft) {
        const steps = (0, ft_pending_1.deriveFtPending)(pad.sessionTaps.map((t) => ({ ...t, sessionId: t.sessionId ?? null })));
        const step = steps[steps.length - 1] ?? { trip: { kind: "unknown" }, tech: null };
        ft.dataset.ftState = step.trip.kind;
        ft.innerHTML = (0, nba_tap_pad_1.renderNbaFtState)(step, pad.teams);
        const areas = (0, nba_tap_pad_1.nbaFtAreas)(step);
        const now = performance.now();
        for (const side of ["away", "home"]) {
            const area = body()?.querySelector(`.nba-ft-area[data-side="${side}"]`);
            if (!area)
                continue;
            const { mode, held, progress: progressText } = areas[side];
            if (mode !== pad.ftMode[side]) {
                pad.ftMode[side] = mode;
                pad.swappedAt[side] = now;
                pad.timers.push(window.setTimeout(refreshStatus, exports.GATE_MS)); // clears the gated look
            }
            area.dataset.mode = mode;
            area.classList.toggle("nba-ft-held", held);
            area.classList.toggle("nba-ft-gated", now - pad.swappedAt[side] < exports.GATE_MS);
            const progress = area.querySelector(".nba-ft-progress");
            if (progress)
                progress.textContent = progressText;
            for (const b of area.querySelectorAll(".nba-ft-swaps"))
                b.disabled = (0, nba_tap_pad_1.nbaFtButtonMode)(b.dataset.tapKind ?? "") !== mode;
        }
    }
    const last = body()?.querySelector(".nba-last");
    if (last)
        last.innerHTML = (0, nba_tap_pad_1.renderNbaLastTap)(pad.history, pad.teams);
    const track = (0, nba_tap_pad_1.renderNbaTrack)({
        synced: pad.sessionSynced,
        sinceMs: pad.trackingSince,
        leftOnSinceMs: (0, nba_tap_pad_1.nbaTrackingLeftOn)(pad.history),
    });
    const btn = body()?.querySelector(".nba-track");
    if (btn) {
        btn.textContent = track.label;
        btn.disabled = track.disabled;
        btn.classList.toggle("nba-track-on", track.on);
    }
    const note = body()?.querySelector(".nba-track-note");
    if (note)
        note.textContent = track.note;
}
function refuse(current, text) {
    current.refusal = { text, until: Date.now() + REFUSAL_MS };
    navigator.vibrate?.([40, 40, 40]);
    refreshStatus();
}
/** A new session, synchronized when the pad has a clock offset; queued behind unsent taps. */
function startSession(current) {
    const now = Date.now();
    current.sessionId = (0, uuid_1.uuidV4)();
    current.sessionSynced = current.clock !== null;
    const start = (0, nba_tap_pad_1.buildNbaTap)({ tapId: (0, uuid_1.uuidV4)(), kind: "session_start", side: null, atMs: now, nowMs: now, clock: current.clock, undoesTapId: null, sessionId: current.sessionId });
    current.queue.add(start);
    current.sessionTaps = [start];
    current.history.push(toHistory(start));
}
function withDeadline(p, ms) {
    let timer;
    const expired = new Promise((_, reject) => {
        timer = window.setTimeout(() => reject(new Error(`no answer in ${ms} ms`)), ms);
    });
    return Promise.race([p, expired]).finally(() => window.clearTimeout(timer));
}
/** A newer sync supersedes this one, whose samples are then dropped; the offset is dated at its sample. */
async function syncClock(current) {
    const run = ++current.syncRun;
    const samples = [];
    for (let i = 0; i < SYNC_SAMPLES; i++) {
        try {
            const t0 = Date.now();
            const serverMs = await withDeadline((0, taps_1.fetchServerClock)(), CLOCK_TIMEOUT_MS);
            samples.push({ t0, t1: Date.now(), serverMs });
        }
        catch {
            break; // offline or no answer: keep what this sync has
        }
        if (current.syncRun !== run)
            return;
    }
    if (current.syncRun !== run || pad !== current)
        return;
    const best = (0, tap_queue_1.estimateClockSample)(samples);
    if (best) {
        current.clock = best;
        if (!current.sessionSynced) {
            startSession(current);
            flush(current);
        }
    }
    refreshStatus();
}
/** Holds the screen on while the pad is open; unsupported or refused, the status line says so. */
async function holdScreen(current) {
    if (current.wakeLock || current.wakeLockAsking || document.visibilityState !== "visible" || !navigator.wakeLock)
        return;
    current.wakeLockAsking = true;
    try {
        const lock = await navigator.wakeLock.request("screen");
        if (pad !== current) {
            void lock.release();
            return;
        }
        current.wakeLock = lock;
        lock.addEventListener("release", () => {
            if (current.wakeLock === lock)
                current.wakeLock = null;
            if (pad === current)
                refreshStatus();
        });
    }
    catch {
        // refused (low power, not allowed): the status line says the screen may sleep
    }
    finally {
        current.wakeLockAsking = false;
    }
    if (pad === current)
        refreshStatus();
}
/** Back online or back to the front: send now, past the backoff's wait, and re-sync the clock. */
function catchUp(current) {
    current.queue.retryNow();
    flush(current);
    void syncClock(current);
}
function flush(current) {
    void current.queue.flush().then(() => {
        if (pad === current)
            refreshStatus();
    });
}
/** A tracking press, timed by its touch like any tap; only once the session is synchronized. */
function onTrackPress(event) {
    if (!pad)
        return;
    event.preventDefault();
    if (!pad.sessionSynced)
        return; // the button is disabled too; this is the rule (§16a)
    const atMs = performance.timeOrigin + event.timeStamp;
    const kind = pad.trackingSince === null ? "track_start" : "track_stop";
    const tap = (0, nba_tap_pad_1.buildNbaTap)({ tapId: (0, uuid_1.uuidV4)(), kind, side: null, atMs, nowMs: Date.now(), clock: pad.clock, undoesTapId: null, sessionId: pad.sessionId });
    pad.queue.add(tap);
    pad.history.push({ ...toHistory(tap), atMs });
    pad.trackingSince = kind === "track_start" ? atMs : null;
    refreshStatus();
    flush(pad);
}
function onPointerDown(event) {
    if (!pad)
        return;
    if (parkedHold.press(event))
        return;
    const track = event.target.closest(".nba-track");
    if (track)
        return onTrackPress(event);
    const btn = event.target.closest(".nba-tap-btn");
    const kind = btn?.dataset.tapKind;
    if (!btn || !isNbaKind(kind) || kind === "session_start")
        return;
    event.preventDefault(); // no text selection, no ghost click, no double-tap zoom
    // The swap gate (§11.1): only this area's swapping buttons, never FT ? or anything else.
    const area = btn.closest(".nba-ft-area");
    const areaSide = area?.dataset.side;
    if (area && btn.classList.contains("nba-ft-swaps") && (areaSide === "home" || areaSide === "away")
        && performance.now() - pad.swappedAt[areaSide] < exports.GATE_MS) {
        navigator.vibrate?.(30);
        return;
    }
    // Some browsers still deliver pointer events to a disabled button.
    if (btn.disabled) {
        return refuse(pad, (0, nba_tap_pad_1.nbaFtButtonMode)(kind) === "tech" ? "no technical pending: tap Tech FT first" : "no free-throw trip open: tap FT ×N first");
    }
    const atMs = performance.timeOrigin + event.timeStamp;
    let undoesTapId = null;
    if (kind === "undo") {
        dropParked(pad);
        const target = (0, nba_tap_pad_1.nbaUndoTarget)(pad.history, pad.sessionId);
        if (!target)
            return refuse(pad, "nothing to undo in this session");
        undoesTapId = target.tapId;
    }
    pad.refusal = null;
    const side = btn.dataset.side === "home" || btn.dataset.side === "away" ? btn.dataset.side : null;
    const tap = (0, nba_tap_pad_1.buildNbaTap)({ tapId: (0, uuid_1.uuidV4)(), kind, side, atMs, nowMs: Date.now(), clock: pad.clock, undoesTapId, sessionId: pad.sessionId });
    pad.queue.add(tap);
    pad.sessionTaps.push(tap);
    pad.history.push({ ...toHistory(tap), atMs });
    btn.classList.add("tap-flash");
    window.setTimeout(() => btn.classList.remove("tap-flash"), 150);
    navigator.vibrate?.(15);
    refreshStatus();
    flush(pad);
    void holdScreen(pad);
}
function onClick(event) {
    if (pad && event.target.closest(".nba-tap-exit"))
        pad.onExit();
}
/** The release that ends a full hold on the parked count copies, inside the gesture. */
function onPointerUp(event) {
    if (!parkedHold.release(event) || !pad)
        return;
    const current = pad;
    void (0, parked_hold_1.copyParked)(current.queue).then(({ ok, text }) => {
        if (pad !== current)
            return;
        current.refusal = { text, until: Date.now() + REFUSAL_MS, ok };
        refreshStatus();
    });
}
const onPointerCancel = (event) => parkedHold.cancel(event);
const onOnline = () => {
    if (pad)
        catchUp(pad);
};
const onVisibility = () => {
    if (!pad || document.visibilityState !== "visible")
        return;
    catchUp(pad);
    void holdScreen(pad);
};
/** Once, from setupWinProbPage: the listeners sit on the shared page body. */
function setupNbaTapPad() {
    body()?.addEventListener("pointerdown", onPointerDown);
    body()?.addEventListener("pointerup", onPointerUp);
    body()?.addEventListener("pointercancel", onPointerCancel);
    body()?.addEventListener("click", onClick);
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibility);
}
function openNbaTapPad(gameId, teams, onExit) {
    closeNbaTapPad();
    const current = {
        gameId,
        sessionId: "",
        sessionSynced: false,
        sessionTaps: [],
        queue: tap_queue_1.TapQueue.shared(`tap-pad:nba:${gameId}`, (taps) => (0, taps_1.postTaps)("nba", gameId, taps), tap_queue_1.browserTapStorage, Date.now, SEND_TIMEOUT_MS),
        teams,
        history: [],
        refusal: null,
        clock: null,
        timers: [],
        onExit,
        ftMode: { home: "trip", away: "trip" },
        swappedAt: { home: Number.NEGATIVE_INFINITY, away: Number.NEGATIVE_INFINITY },
        wakeLock: null,
        wakeLockAsking: false,
        syncRun: 0,
        trackingSince: null,
    };
    pad = current;
    const root = body();
    if (root)
        root.innerHTML = (0, nba_tap_pad_1.renderNbaTapPad)(teams, `${teams.away} @ ${teams.home}`);
    // Unsent taps first, then the server's record; UNDO stays within this session.
    current.history = current.queue.unsent.map(toHistory);
    startSession(current); // unsynchronized: no clock yet; the first sync starts a synchronized one
    refreshStatus();
    void (0, taps_1.fetchTaps)("nba", gameId)
        .then((stored) => {
        if (pad !== current)
            return;
        const known = new Set(current.history.map((t) => t.tapId));
        const merged = [...current.history, ...stored.filter((t) => !known.has(t.tapId)).map(toHistory)];
        current.history = merged.sort((a, b) => a.atMs - b.atMs);
        refreshStatus();
    })
        .catch(() => undefined); // offline: the pad still works from local state
    void syncClock(current);
    current.timers.push(window.setInterval(() => flush(current), FLUSH_MS), window.setInterval(() => void syncClock(current), SYNC_MS), window.setInterval(refreshStatus, 1000));
    flush(current); // taps left unsent before a reload go out now
    void holdScreen(current);
}
function closeNbaTapPad() {
    if (!pad)
        return;
    for (const t of pad.timers) {
        window.clearInterval(t);
        window.clearTimeout(t);
    }
    void pad.queue.flush(); // last attempt; anything left stays in storage
    void pad.wakeLock?.release();
    parkedHold.reset();
    pad = null;
}
